import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router';

import { ApplicationRoutes } from '../../../../application/constants/routes';
import { IMainLayoutDomainStore } from '../../../layout/main/store/domain/IMainLayoutDomainStore';
import { UnitHeader } from '../../../shared/components/UnitHeader';
import { LearningUnitPreviewFooter } from './components/LearningUnitPreviewFooter';
import { PreviewFinalResults, PreviewPractice, PreviewQuiz, PreviewTheory } from './components/LearningUnitPreviewMain';
import { LearningUnitPreviewSidebar } from './components/LearningUnitPreviewSidebar';
import { LearningUnitPreviewPageContext } from './store/LearningUnitPreviewPageContext';
import { ILearningUnitPreviewPageDomain, LearningUnitPreviewPageDomain } from './store/LearningUnitPreviewPageDomain';
import { Colors } from '../../../design/color/Colors';

const STEPS = {
  theory: <PreviewTheory />,
  quiz: <PreviewQuiz />,
  practice: <PreviewPractice />,
  finalResults: <PreviewFinalResults />,
};

export const LearningUnitPreviewPage = observer(({ layoutDomain }: { layoutDomain: IMainLayoutDomainStore }) => {
  const [unitPreviewDomain] = useState<ILearningUnitPreviewPageDomain>(new LearningUnitPreviewPageDomain(layoutDomain));
  const [showSidebar, setShowSidebar] = useState(true);
  const { unitId: id } = useParams<{ unitId: string }>();
  const currentStep = unitPreviewDomain?.ui?.currentStep.value as string;
  const classes = useStyles({ currentStep });
  const unit = unitPreviewDomain?.ui.unit.entity;

  useEffect(() => {
    unitPreviewDomain.loadData(id);
  }, []);

  return (
    <LearningUnitPreviewPageContext.Provider value={{ unitPreviewDomain, showSidebar, setShowSidebar }}>
      <div className={classes.wrapper}>
        <UnitHeader
          classes={classes.header}
          title={unit?.settings?.titleHeading}
          backLink={generatePath(ApplicationRoutes.LearningUnitEdit, { unitId: String(unit?.id) })}
          backButtonTitle="edit"
        />
        <div className={classes.container}>
          <main className={classes.main}>
            <div className={classes.mainContent}>{STEPS[currentStep]}</div>
            <LearningUnitPreviewFooter />
          </main>
          {showSidebar && (
            <nav className={classes.sidebar}>
              <LearningUnitPreviewSidebar />
            </nav>
          )}
        </div>
      </div>
    </LearningUnitPreviewPageContext.Provider>
  );
});
interface StyleProps {
  currentStep: string;
}

export const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  container: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('md')]: { flexDirection: 'row' },
  },
  main: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${Colors.border}`,
    borderRight: 'none',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: { borderRight: `1px solid ${Colors.border}` },
  },
  mainContent: {
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    height: (props) => (props.currentStep === 'practice' ? '100%' : 'auto'),
    '& > div > a > .MuiBox-root ': {
      overflowY: 'hidden',
      height: 'auto',
    },
  },
  sidebar: {
    overflowY: 'auto',
    borderTop: `1px solid ${Colors.border}`,
    padding: '16px',
    [theme.breakpoints.up('md')]: { width: '372px' },
  },
  header: {
    paddingTop: '16px',
  },
}));
